import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from "recharts";
import { TagCloud } from "react-tagcloud";
import { TrendingUp, MessageSquare, BarChart3, Cloud, FileText, CheckCircle, Star } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getIdToken } from "@/lib/auth";

interface Product {
  id: string;
  name: string;
}

interface CustomForm {
  id: string;
  name: string;
  productId: string;
  product: {
    id: string;
    name: string;
  };
  questions: CustomFormQuestion[];
}

interface CustomFormQuestion {
  id: string;
  text: string;
  type: 'OPEN_TEXT' | 'MULTIPLE_CHOICE' | 'RATING' | 'YES_NO';
  enableSentimentAnalysis: boolean;
  enableKeywordExtraction: boolean;
  options: Array<{
    id: string;
    text: string;
    value: string;
  }>;
}

interface SentimentDistribution {
  sentiment: string;
  count: number;
  percentage: number;
}

interface WordCloudTerm {
  word: string;
  count: number;
}

interface TrendPoint {
  date: string;
  positive: number;
  neutral: number;
  negative: number;
  averageScore?: number;
}

interface VolumePoint {
  date: string;
  count: number;
}

interface CustomFormInsights {
  formMetrics: {
    totalResponses: number;
    averageCompletionRate: number;
    totalQuestions: number;
  };
  questionInsights: Array<{
    questionId: string;
    questionText: string;
    questionType: string;
    sentimentDistribution?: SentimentDistribution[];
    wordCloudData?: WordCloudTerm[];
    multipleChoiceData?: Array<{ option: string; count: number }>;
    ratingData?: {
      averageRating: number;
      totalResponses: number;
      distribution: Array<{ rating: number; count: number }>;
    };
    yesNoData?: Array<{ option: string; count: number }>;
  }>;
}

const COLORS = {
  positive: "#10b981",
  neutral: "#6b7280", 
  negative: "#ef4444"
};

const InsightsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("month");
  const [loading, setLoading] = useState(false);
  const [sentimentData, setSentimentData] = useState<SentimentDistribution[]>([]);
  const [wordCloudData, setWordCloudData] = useState<WordCloudTerm[]>([]);
  const [sentimentTrendData, setSentimentTrendData] = useState<TrendPoint[]>([]);
  const [volumeTrendData, setVolumeTrendData] = useState<VolumePoint[]>([]);
  
  // Custom Forms state
  const [customForms, setCustomForms] = useState<CustomForm[]>([]);
  const [selectedCustomForm, setSelectedCustomForm] = useState<string>("");
  const [customFormInsights, setCustomFormInsights] = useState<CustomFormInsights | null>(null);
  const [customFormsLoading, setCustomFormsLoading] = useState(false);
  
  const { toast } = useToast();

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await fetch(`${import.meta.env.VITE_API_URL}/products`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProducts(data);
          
          // Check if the currently selected product still exists
          if (selectedProduct && !data.find((p: Product) => p.id === selectedProduct)) {
            // Selected product no longer exists, clear the selection
            setSelectedProduct("");
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        toast({
          title: "Error",
          description: "Failed to fetch products",
          variant: "destructive",
        });
      }
    };

    fetchProducts();
  }, [toast]);

  // Fetch custom forms when product changes
  useEffect(() => {
    const fetchCustomForms = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await fetch(`${import.meta.env.VITE_API_URL}/custom-forms`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCustomForms(data);
          
          // Clear selected form if it's not in the new list
          if (selectedCustomForm && !data.find((f: CustomForm) => f.id === selectedCustomForm)) {
            setSelectedCustomForm("");
          }
        }
      } catch (error) {
        console.error('Error fetching custom forms:', error);
        toast({
          title: "Error",
          description: "Failed to fetch custom forms",
          variant: "destructive",
        });
      }
    };

    fetchCustomForms();
  }, [toast]);

  // Fetch insights data
  useEffect(() => {
    if (!selectedProduct) return;

    const fetchInsights = async () => {
      setLoading(true);
      try {
        const token = await getIdToken();
        if (!token) return;

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        // Fetch all insights data in parallel
        const [sentimentRes, wordCloudRes, sentimentTrendRes, volumeTrendRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/insights/sentiment-distribution?productId=${selectedProduct}&period=${selectedPeriod}`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/insights/word-cloud?productId=${selectedProduct}&period=${selectedPeriod}&limit=20`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/insights/sentiment-trend?productId=${selectedProduct}&period=${selectedPeriod}&interval=day`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/insights/feedback-volume-trend?productId=${selectedProduct}&period=${selectedPeriod}&interval=day`, { headers })
        ]);

        if (sentimentRes.ok) {
          const sentimentData = await sentimentRes.json();
          setSentimentData(sentimentData.distribution);
        }

        if (wordCloudRes.ok) {
          const wordCloudData = await wordCloudRes.json();
          setWordCloudData(wordCloudData.terms);
        }

        if (sentimentTrendRes.ok) {
          const sentimentTrendData = await sentimentTrendRes.json();
          setSentimentTrendData(sentimentTrendData.trend);
        }

        if (volumeTrendRes.ok) {
          const volumeTrendData = await volumeTrendRes.json();
          setVolumeTrendData(volumeTrendData.trend);
        }

      } catch (error) {
        console.error('Error fetching insights:', error);
        toast({
          title: "Error",
          description: "Failed to fetch insights data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [selectedProduct, selectedPeriod, toast]);

  // Fetch custom form insights
  useEffect(() => {
    if (!selectedCustomForm) {
      setCustomFormInsights(null);
      return;
    }

    const fetchCustomFormInsights = async () => {
      setCustomFormsLoading(true);
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await fetch(`${import.meta.env.VITE_API_URL}/insights/custom-form/${selectedCustomForm}?period=${selectedPeriod}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCustomFormInsights(data);
        } else {
          throw new Error('Failed to fetch custom form insights');
        }
      } catch (error) {
        console.error('Error fetching custom form insights:', error);
        toast({
          title: "Error",
          description: "Failed to fetch custom form insights",
          variant: "destructive",
        });
      } finally {
        setCustomFormsLoading(false);
      }
    };

    fetchCustomFormInsights();
  }, [selectedCustomForm, selectedPeriod, toast]);

  const tagCloudOptions = {
    minSize: 12,
    maxSize: 35,
    shuffle: false,
    colorOptions: {
      luminosity: 'dark',
      hue: 'blue'
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTotalFeedback = () => {
    return sentimentData.reduce((total, item) => total + item.count, 0);
  };

  const getAverageSentiment = () => {
    const total = getTotalFeedback();
    if (total === 0) return 0;
    
    const weightedSum = sentimentData.reduce((sum, item) => {
      const score = item.sentiment === 'positive' ? 5 : item.sentiment === 'neutral' ? 3 : 1;
      return sum + (item.count * score);
    }, 0);
    
    return (weightedSum / total).toFixed(1);
  };

  const renderGenericFeedbackInsights = () => (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Select product and time period for analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Product</label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Time Period</label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Last Week</SelectItem>
                  <SelectItem value="month">Last Month</SelectItem>
                  <SelectItem value="6months">Last 6 Months</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      {selectedProduct ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getTotalFeedback()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Sentiment</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getAverageSentiment()}</div>
            <p className="text-xs text-muted-foreground">out of 5.0</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positive Sentiment</CardTitle>
            <Badge variant="secondary" className="bg-amber-100 text-amber-900">Positive</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sentimentData.find(s => s.sentiment === 'positive')?.count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {sentimentData.find(s => s.sentiment === 'positive')?.percentage.toFixed(1) || 0}% of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Neutral Sentiment</CardTitle>
            <Badge variant="secondary" className="bg-gray-100 text-gray-800">Neutral</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sentimentData.find(s => s.sentiment === 'neutral')?.count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {sentimentData.find(s => s.sentiment === 'neutral')?.percentage.toFixed(1) || 0}% of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Negative Sentiment</CardTitle>
            <Badge variant="secondary" className="bg-red-100 text-red-800">Negative</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sentimentData.find(s => s.sentiment === 'negative')?.count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {sentimentData.find(s => s.sentiment === 'negative')?.percentage.toFixed(1) || 0}% of total
            </p>
          </CardContent>
        </Card>
      </div>
      ) : (
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-muted-foreground">Please select a product to view insights</p>
          </CardContent>
        </Card>
      )}

      {/* Charts Grid */}
      {selectedProduct && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sentiment Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Sentiment Distribution</CardTitle>
            <CardDescription>Breakdown of feedback by sentiment</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-64 flex items-center justify-center">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sentimentData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ sentiment, percentage }) => `${sentiment} (${percentage.toFixed(1)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {sentimentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.sentiment as keyof typeof COLORS]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Word Cloud */}
        <Card>
          <CardHeader>
            <CardTitle>Most Common Words</CardTitle>
            <CardDescription>Frequently mentioned terms in feedback</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-64 flex items-center justify-center">Loading...</div>
            ) : wordCloudData.length > 0 ? (
              <div className="h-64 flex items-center justify-center">
                <TagCloud 
                  minSize={12}
                  maxSize={35}
                  tags={wordCloudData.map(item => ({
                    value: item.word,
                    count: item.count
                  }))}
                  className="text-center"
                />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No word cloud data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sentiment Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Sentiment Trends Over Time</CardTitle>
            <CardDescription>How sentiment has changed over the selected period</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-64 flex items-center justify-center">Loading...</div>
            ) : sentimentTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sentimentTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={formatDate}
                    formatter={(value, name) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="positive" 
                    stroke={COLORS.positive} 
                    strokeWidth={2}
                    dot={{ fill: COLORS.positive, strokeWidth: 2, r: 4 }}
                    name="Positive"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="neutral" 
                    stroke={COLORS.neutral} 
                    strokeWidth={2}
                    dot={{ fill: COLORS.neutral, strokeWidth: 2, r: 4 }}
                    name="Neutral"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="negative" 
                    stroke={COLORS.negative} 
                    strokeWidth={2}
                    dot={{ fill: COLORS.negative, strokeWidth: 2, r: 4 }}
                    name="Negative"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Volume */}
        <Card>
          <CardHeader>
            <CardTitle>Feedback Volume Over Time</CardTitle>
            <CardDescription>Number of feedback submissions over time</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-64 flex items-center justify-center">Loading...</div>
            ) : volumeTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={volumeTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={formatDate}
                    formatter={(value) => [value, 'Feedback Count']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No volume data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );

  const renderCustomFormsInsights = () => (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Select custom form and time period for analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Custom Form</label>
              <Select value={selectedCustomForm} onValueChange={setSelectedCustomForm}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a custom form" />
                </SelectTrigger>
                <SelectContent>
                  {customForms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name} ({form.product.name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Time Period</label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Last Week</SelectItem>
                  <SelectItem value="month">Last Month</SelectItem>
                  <SelectItem value="6months">Last 6 Months</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Metrics */}
      {selectedCustomForm && customFormInsights ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Responses</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{customFormInsights.formMetrics.totalResponses}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(customFormInsights.formMetrics.averageCompletionRate * 100).toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground">
                Average questions answered
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{customFormInsights.formMetrics.totalQuestions}</div>
            </CardContent>
          </Card>
        </div>
      ) : !selectedCustomForm ? (
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-muted-foreground">Please select a custom form to view insights</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Question Insights */}
      {selectedCustomForm && customFormInsights && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Question Insights</h2>
          {customFormInsights.questionInsights.map((questionInsight, index) => (
            <Card key={questionInsight.questionId}>
              <CardHeader>
                <CardTitle className="text-lg">Question {index + 1}: {questionInsight.questionText}</CardTitle>
                <CardDescription>Type: {questionInsight.questionType.replace('_', ' ')}</CardDescription>
              </CardHeader>
              <CardContent>
                {customFormsLoading ? (
                  <div className="h-64 flex items-center justify-center">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Open Text Insights */}
                    {questionInsight.questionType === 'OPEN_TEXT' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {questionInsight.sentimentDistribution && questionInsight.sentimentDistribution.length > 0 ? (
                          <div>
                            <h4 className="font-medium mb-2">Sentiment Distribution</h4>
                            <ResponsiveContainer width="100%" height={200}>
                              <PieChart>
                                <Pie
                                  data={questionInsight.sentimentDistribution}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ sentiment, percentage }) => `${sentiment} (${percentage.toFixed(1)}%)`}
                                  outerRadius={60}
                                  fill="#8884d8"
                                  dataKey="count"
                                >
                                  {questionInsight.sentimentDistribution.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={COLORS[entry.sentiment as keyof typeof COLORS]} />
                                  ))}
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-32">
                            <p className="text-muted-foreground">Sentiment analysis not enabled for this question</p>
                          </div>
                        )}
                        
                        {questionInsight.wordCloudData && questionInsight.wordCloudData.length > 0 ? (
                          <div>
                            <h4 className="font-medium mb-2">Most Common Words</h4>
                            <div className="h-48 flex items-center justify-center">
                              <TagCloud 
                                minSize={12}
                                maxSize={30}
                                tags={questionInsight.wordCloudData.map(item => ({
                                  value: item.word,
                                  count: item.count
                                }))}
                                className="text-center"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-32">
                            <p className="text-muted-foreground">No word cloud data available</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Multiple Choice Insights */}
                    {questionInsight.questionType === 'MULTIPLE_CHOICE' && questionInsight.multipleChoiceData && (
                      <div>
                        <h4 className="font-medium mb-2">Response Distribution</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={questionInsight.multipleChoiceData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="option" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Rating Insights */}
                    {questionInsight.questionType === 'RATING' && questionInsight.ratingData && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium mb-2">Average Rating</h4>
                          <div className="text-center">
                            <div className="text-4xl font-bold text-primary">
                              {questionInsight.ratingData.averageRating.toFixed(1)}
                            </div>
                            <div className="flex justify-center mt-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-6 w-6 ${
                                    star <= questionInsight.ratingData!.averageRating
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              Based on {questionInsight.ratingData.totalResponses} responses
                            </p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Rating Distribution</h4>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={questionInsight.ratingData.distribution}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="rating" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="count" fill="#10b981" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Yes/No Insights */}
                    {questionInsight.questionType === 'YES_NO' && questionInsight.yesNoData && (
                      <div>
                        <h4 className="font-medium mb-2">Response Distribution</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={questionInsight.yesNoData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ option, count }) => `${option} (${count})`}
                              outerRadius={60}
                              fill="#8884d8"
                              dataKey="count"
                            >
                              {questionInsight.yesNoData.map((entry, idx) => (
                                <Cell key={`cell-${idx}`} fill={entry.option === 'Yes' ? '#10b981' : '#ef4444'} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground">
            Analyze feedback patterns and trends for your products
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="generic" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generic">Generic Feedback</TabsTrigger>
          <TabsTrigger value="custom-forms">Custom Forms</TabsTrigger>
        </TabsList>
        <TabsContent value="generic" className="space-y-6">
          {renderGenericFeedbackInsights()}
        </TabsContent>
        <TabsContent value="custom-forms" className="space-y-6">
          {renderCustomFormsInsights()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InsightsPage; 